import { ParagraphTextRange } from 'src/models/Text'
import { TranscriptParagraph } from 'src/models/Transcript'

// The statement offset is chunk-retrieval start, not an utterance boundary: in production it has landed on the
// last utterance of an exchange whose cited question sat three paragraphs earlier.
const MAX_STATEMENT_ANCHOR_PARAGRAPH_PROBE = 3

// A turn's own offset is real timing, off only by the flooring to whole seconds, which can reach the neighbouring
// paragraph and no further.
const MAX_TURN_OFFSET_PARAGRAPH_PROBE = 1

// The backend skips filler utterances between the spans it cites ("Tons wise." / "Yeah, sure." between a question
// and "1%.").
const MAX_PARAGRAPH_GAP_BETWEEN_TURNS = 3

// "No." and "Yes." recur throughout a deposition, so a turn this short is only ever placed relative to a longer
// one and never opens the search.
const SHORT_TURN_MAX_LENGTH = 16

type Placement = {
    range: ParagraphTextRange
    paragraphIndex: number
}

type ReadParagraph = (index: number) => TranscriptParagraph | undefined

export type QuoteTurnQuery = {
    text: string
    /** Where this turn's own offset resolves; absent when the backend supplied no timing for it. */
    anchorParagraphIndex?: number
}

/** Diagnostics: tells a bad offset from a text mismatch after the fact. */
export type QuoteTurnResolution = {
    turnIndex: number
    searchedFromParagraphIndex: number
    /** Null when the turn was dropped. */
    resolvedParagraphIndex: number | null
}

type LocateQuoteTurnsParams = {
    // Statement-level anchor, used for any turn without one of its own.
    anchorParagraphIndex: number
    turns: QuoteTurnQuery[]
    totalParagraphCount: number
    getParagraphByIndex: ReadParagraph
    onTurnResolved?: (resolution: QuoteTurnResolution) => void
}

type TurnSearch = {
    searchedFromParagraphIndex: number
    placement: Placement | null
}

/**
 * Resolves cited turns to single-paragraph ranges. A turn's own offset decides which paragraph it goes in; a turn
 * the backend gave no offset for is placed from the statement anchor, then a forward-only cursor.
 */
export const locateQuoteTurns = (params: LocateQuoteTurnsParams): ParagraphTextRange[] => {
    const { anchorParagraphIndex, turns, totalParagraphCount, getParagraphByIndex, onTurnResolved } = params

    const normalisedTurns = turns
        .map((turn, turnIndex) => ({ ...turn, turnIndex, text: turn.text.trim().toLowerCase() }))
        .filter(({ text }) => text.length > 0)
    const isAnchorInTranscript = anchorParagraphIndex >= 0 && anchorParagraphIndex < totalParagraphCount

    if (!normalisedTurns.length || !isAnchorInTranscript) {
        return []
    }

    const readParagraph: ReadParagraph = (index) => (index >= 0 && index < totalParagraphCount ? getParagraphByIndex(index) : undefined)

    const ranges: ParagraphTextRange[] = []
    let cursor: Placement | null = null

    for (const turn of normalisedTurns) {
        const { searchedFromParagraphIndex, placement } = findTurn(turn, cursor, anchorParagraphIndex, readParagraph)

        onTurnResolved?.({
            turnIndex: turn.turnIndex,
            searchedFromParagraphIndex,
            resolvedParagraphIndex: placement?.paragraphIndex ?? null,
        })

        if (placement) {
            ranges.push(placement.range)
            cursor = placement
        }
    }

    return ranges
}

const findTurn = (
    turn: QuoteTurnQuery,
    cursor: Placement | null,
    statementAnchorParagraphIndex: number,
    readParagraph: ReadParagraph,
): TurnSearch => {
    if (turn.anchorParagraphIndex !== undefined) {
        return {
            searchedFromParagraphIndex: turn.anchorParagraphIndex,
            placement: findTurnNearAnchor(turn.text, turn.anchorParagraphIndex, MAX_TURN_OFFSET_PARAGRAPH_PROBE, cursor, readParagraph),
        }
    }

    if (cursor) {
        return { searchedFromParagraphIndex: cursor.paragraphIndex, placement: findTurnAfterCursor(turn.text, cursor, readParagraph) }
    }

    return {
        searchedFromParagraphIndex: statementAnchorParagraphIndex,
        placement: findTurnNearAnchor(
            turn.text,
            statementAnchorParagraphIndex,
            MAX_STATEMENT_ANCHOR_PARAGRAPH_PROBE,
            cursor,
            readParagraph,
        ),
    }
}

const findTurnNearAnchor = (
    turn: string,
    anchorParagraphIndex: number,
    maxProbe: number,
    cursor: Placement | null,
    readParagraph: ReadParagraph,
): Placement | null => {
    const probe = turn.length <= SHORT_TURN_MAX_LENGTH ? 0 : maxProbe

    for (const distance of buildProbeDistances(probe)) {
        const paragraphIndex = anchorParagraphIndex + distance
        // An offset names a paragraph, never a position inside one, so two turns sharing a paragraph would
        // otherwise both match its first occurrence.
        const fromCharOffset = paragraphIndex === cursor?.paragraphIndex ? cursor.range.end : 0
        const placement = findTurnInParagraph(readParagraph(paragraphIndex), paragraphIndex, turn, fromCharOffset)

        if (placement) {
            return placement
        }
    }

    return null
}

const findTurnAfterCursor = (turn: string, cursor: Placement, readParagraph: ReadParagraph): Placement | null => {
    for (let gap = 0; gap <= MAX_PARAGRAPH_GAP_BETWEEN_TURNS; gap++) {
        const paragraphIndex = cursor.paragraphIndex + gap
        const fromCharOffset = gap === 0 ? cursor.range.end : 0
        const placement = findTurnInParagraph(readParagraph(paragraphIndex), paragraphIndex, turn, fromCharOffset)

        if (placement) {
            return placement
        }
    }

    return null
}

const findTurnInParagraph = (
    paragraph: TranscriptParagraph | undefined,
    paragraphIndex: number,
    turn: string,
    fromCharOffset: number,
): Placement | null => {
    if (!paragraph?.content.length) {
        return null
    }

    const matchStart = TranscriptParagraph.textOf(paragraph).toLowerCase().indexOf(turn, fromCharOffset)

    if (matchStart === -1) {
        return null
    }

    return {
        range: { paragraphId: paragraph.id, start: matchStart, end: matchStart + turn.length },
        paragraphIndex,
    }
}

// Nearest-first, forward-biased — chunk offsets point at chunk start, so the quote usually follows.
const buildProbeDistances = (probe: number): number[] => {
    const distances = [0]

    for (let distance = 1; distance <= probe; distance++) {
        distances.push(distance, -distance)
    }

    return distances
}
